import React, { useState, useEffect } from 'react';
import { X, Check, UserPlus } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { FollowRequest } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';

interface FollowRequestsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FollowRequestsModal: React.FC<FollowRequestsModalProps> = ({
  isOpen,
  onClose
}) => {
  const { user } = useAuthStore();
  const [requests, setRequests] = useState<FollowRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen && user) {
      loadFollowRequests();
    }
  }, [isOpen, user]);

  const loadFollowRequests = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('follow_requests')
        .select(`
          *,
          requester:profiles!follow_requests_requester_id_fkey (
            id,
            username,
            full_name,
            avatar_url,
            bio
          )
        `)
        .eq('requested_id', user?.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedRequests = (data || []).map(request => ({
        id: request.id,
        requester_id: request.requester_id,
        requested_id: request.requested_id,
        status: request.status,
        created_at: request.created_at,
        requester: {
          id: request.requester.id,
          username: request.requester.username,
          displayName: request.requester.full_name || request.requester.username,
          profileImage: request.requester.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
          bio: request.requester.bio || '',
          followerCount: 0,
          followingCount: 0,
          postCount: 0,
        }
      }));

      setRequests(transformedRequests);
    } catch (error) {
      console.error('Error loading follow requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (requestId: string, action: 'accept' | 'reject') => {
    setProcessingIds(prev => new Set(prev).add(requestId));
    
    try {
      if (action === 'accept') {
        // Accept the request by creating a follow relationship
        const request = requests.find(r => r.id === requestId);
        if (request) {
          const { error: followError } = await supabase
            .from('follows')
            .insert([{
              follower_id: request.requester_id,
              following_id: request.requested_id
            }]);

          if (followError) throw followError;
        }
      }

      // Update request status
      const { error } = await supabase
        .from('follow_requests')
        .update({ status: action === 'accept' ? 'accepted' : 'rejected' })
        .eq('id', requestId);

      if (error) throw error;

      // Remove from local state
      setRequests(prev => prev.filter(request => request.id !== requestId));
    } catch (error) {
      console.error(`Error ${action}ing follow request:`, error);
      alert(`Failed to ${action} follow request. Please try again.`);
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    return `${Math.floor(diffInSeconds / 86400)}d`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Follow Requests">
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
          </div>
        ) : requests.length > 0 ? (
          <div className="space-y-4">
            {requests.map((request) => (
              <div key={request.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center">
                  <img
                    src={request.requester.profileImage}
                    alt={request.requester.username}
                    className="h-12 w-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <p className="font-medium text-gray-900">{request.requester.username}</p>
                    <p className="text-sm text-gray-500">{request.requester.displayName}</p>
                    {request.requester.bio && (
                      <p className="text-xs text-gray-400 mt-1 max-w-xs truncate">
                        {request.requester.bio}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {formatTimeAgo(request.created_at)} ago
                    </p>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleRequest(request.id, 'accept')}
                    disabled={processingIds.has(request.id)}
                    isLoading={processingIds.has(request.id)}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRequest(request.id, 'reject')}
                    disabled={processingIds.has(request.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <UserPlus className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No follow requests</p>
            <p className="text-sm">When someone requests to follow you, you'll see it here.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};